import { useId } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM
} from "@/libraries/design/foundations/grid/src/base";
import {
    RichTextComponent,
    SECTION_DIVIDER_HEIGHTS,
    type SectionDividerType
} from "@/libraries/design/utils";
import { isDarkColor } from "@/utils/colors/colors";
import { useWacRefresh } from "@/utils/wac-refresh/wac-refresh-context";

import { DataEventProps } from "../../../../wpl/foundations/src/index";
import {
    Button,
    ButtonProps,
    ButtonSize,
    ButtonVariant,
    ResponsiveButtonSizes
} from "../../button/src/button";
import { Image, ImageProps } from "../../image/src/image";
import { Link, LinkProps, BgColors } from "../../link/src/link";
export interface ButtonAndLinkProps {
    button: ButtonProps;
    buttonText: string;
    buttonVariant?: ButtonVariant;
    responsiveSize?: ResponsiveButtonSizes;
    dataEventProps?: DataEventProps;
    link?: LinkProps;
}

export interface OneLinkProps {
    link: LinkProps;
}

export type ContentBlockCtaProps = ButtonAndLinkProps | OneLinkProps;

export type ContentBlockTitleAs = "h2" | "h3" | "h4" | "h5" | "p";
export type ContentBlockTitleAppearsAs =
    | "h2"
    | "h3"
    | "h4"
    | "card-title"
    | "eyebrow-md";
export type ContentBlockAlignment = "start" | "center";

export type ResponsiveContentBlockAlignment = {
    mobile: ContentBlockAlignment;
    tablet: ContentBlockAlignment;
    desktop: ContentBlockAlignment;
};

export type IconProps = {
    linkedIcon?: {
        url?: string;
        size?: IconSize;
        alt?: string;
        paddingIncluded?: boolean;
    };
    iconComponent?: JSX.Element;
};

export type ContentBlockImageAspectRatio = "16 / 9" | "1 / 1";

export type ContentBlockColumns = 10 | 12; // Currently only 10 or 12 columns are supported

export const ContentBlockLogoLockups = {
    teamworkCollection: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/20d60pj8j2ju10y33n8jslya4i4a0125.svg",
        height: 62.5,
        alt: "Teamwork Collection logo"
    },
    jira: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1m4xip711l01021jmk8k33gj82grm078.svg",
        height: 32,
        alt: "Jira logo"
    },
    jiraProductDiscovery: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/lnuk8307fp65406pfvj4radajgkw48c5.svg",
        height: 32,
        alt: "Jira Product Discovery logo"
    },
    jiraServiceManagement: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1x20y6tg7hdjl45x6l8a4iy22n3vhff2.svg",
        height: 32,
        alt: "Jira Service Management logo"
    },
    statuspage: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/r237roxi4i14gj1i4t362kw1g06h63tv.svg",
        height: 32,
        alt: "Statuspage logo"
    },
    confluence: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/ou687o50873egmj4l236et86h6178ylm.svg",
        height: 32,
        alt: "Confluence logo"
    },
    trello: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/gf3q277c3idu533i28bg3855yp2mwrlg.svg",
        height: 32,
        alt: "Trello logo"
    },
    loom: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5ncpek557ni2al3s7s5v0dmkcs4ogt05.svg",
        height: 32,
        alt: "Loom logo"
    },
    bitbucket: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/xqweg4il0228mpa8ega4i5hi78p0k16v.svg",
        height: 32,
        alt: "Bitbucket logo"
    },
    rovoDev: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/d888ipg7v6w1c6x4xfb578gfiw5238d0.svg",
        height: 32,
        alt: "Rovo Dev logo"
    },
    sourcetree: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/01u3w2aypa025m62s70p8uo6u21qg3i2.svg",
        height: 32,
        alt: "Sourcetree logo"
    },
    compass: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/yiu46gin8eoxy7f57m1g76a6b22uuf1b.svg",
        height: 32,
        alt: "Compass logo"
    },
    guard: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7085415d2l112j5j545y8nv6ir6t262w.svg",
        height: 32,
        alt: "Guard logo"
    }
};

export interface ContentBlockProps {
    logo?: keyof typeof ContentBlockLogoLockups;
    eyebrow?: string;
    title?: string;
    text?: string | React.ReactElement<typeof RichTextComponent>;
    titleAs?: ContentBlockTitleAs;
    titleAppearsAs?: ContentBlockTitleAppearsAs;
    imageProps?: ImageProps;
    imageAspectRatio?: ContentBlockImageAspectRatio;
    spotIllustrationUrl?: string;
    icon?: IconProps;
    callToActionProps?: ContentBlockCtaProps;
    alignment?: ContentBlockAlignment | ResponsiveContentBlockAlignment;
    orientation?: "horizontal" | "vertical" | "horizontal-reverse";
    columns?: ContentBlockColumns;
    backgroundColor?: string;
    hat?: SectionDividerType;
    pants?: SectionDividerType;
}

interface RenderCtaProps {
    ctaProps: ContentBlockCtaProps;
    buttonSize: ButtonSize;
    alignment: ContentBlockAlignment | ResponsiveContentBlockAlignment;
    backgroundColor?: string;
    titleId?: string;
    callToActionId?: string;
}

const zeroMarginCss = css({ margin: 0 });

const linkFocusOutlineBufferStyles = css({
    paddingInlineStart: token("space.050"),
    paddingBlockEnd: token("space.050")
});

const wordWrapStyles = css({
    overflowWrap: "break-word",
    textWrap: "pretty"
});

type IconSize = "x-small" | "small" | "medium" | "large";

const iconSizesPaddingExcluded: Record<IconSize, number> = {
    "x-small": 24,
    "small": 32,
    "medium": 48,
    "large": 64
};

const iconSizesPaddingIncluded: Record<IconSize, number> = {
    "x-small": 32,
    "small": 48,
    "medium": 88,
    "large": 128
};

type contentBlockColumnsInfo = "assetSpan" | "contentStart";

const contentColumnsLayout: Record<
    ContentBlockColumns,
    Record<contentBlockColumnsInfo, number>
> = {
    10: { assetSpan: 3, contentStart: 2 }, // 10 columns content block means 3 columns for asset, 7 for text
    12: { assetSpan: 4, contentStart: 1 }
    // Add more mappings as needed in the future, ex:
    // 8: 2 // 8 columns content block means 2 columns for asset, 6 for text
};

const renderCta = ({
    ctaProps,
    buttonSize,
    alignment,
    backgroundColor,
    titleId,
    callToActionId
}: // eslint-disable-next-line complexity
RenderCtaProps) => {
    if ("button" in ctaProps && ctaProps.button !== undefined) {
        return (
            <div
                css={css({
                    margin: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: token("space.250"),
                    alignItems: "center",
                    justifyContent:
                        typeof alignment === "string"
                            ? alignment
                            : alignment.mobile,
                    [media.above.sm]: {
                        justifyContent:
                            typeof alignment === "string"
                                ? alignment
                                : alignment.tablet
                    },
                    [media.above.md]: {
                        justifyContent:
                            typeof alignment === "string"
                                ? alignment
                                : alignment.desktop
                    }
                })}
            >
                <Button
                    {...ctaProps.button}
                    size={buttonSize}
                    variant={
                        backgroundColor &&
                        backgroundColor === "#1868DB" &&
                        (ctaProps.buttonVariant === "primary" ||
                            !ctaProps.buttonVariant)
                            ? "secondary"
                            : ctaProps.buttonVariant ?? "primary"
                    }
                    responsiveSize={ctaProps.responsiveSize}
                    colorAgainst={
                        backgroundColor === "#1868DB" ? "other" : "white"
                    }
                    ariaLabelledby={`${callToActionId} ${titleId}`}
                    dataEventProps={ctaProps.dataEventProps}
                    css={css({ flexBasis: "max-content" })}
                >
                    {ctaProps.buttonText}
                </Button>
                {ctaProps.link && (
                    <div css={linkFocusOutlineBufferStyles}>
                        <Link
                            id={callToActionId}
                            ariaLabelledby={`${callToActionId} ${titleId}`}
                            type="primary"
                            bgColor={
                                (isDarkBackgroundColor(backgroundColor)
                                    ? "bgBlackBlue"
                                    : "bgWhite") as BgColors
                            }
                            aria-label={ctaProps.link.text}
                            {...ctaProps.link}
                        />
                    </div>
                )}
            </div>
        );
    }

    if (!ctaProps.link) {
        return null;
    }

    return (
        <div css={[zeroMarginCss, linkFocusOutlineBufferStyles]}>
            <Link
                id={callToActionId}
                ariaLabelledby={`${callToActionId} ${titleId}`}
                type="primary"
                bgColor={
                    (isDarkBackgroundColor(backgroundColor)
                        ? "bgBlackBlue"
                        : "bgWhite") as BgColors
                }
                aria-label={ctaProps.link.text}
                {...ctaProps.link}
            />
        </div>
    );
};

const renderIcon = ({
    linkedIcon: {
        size = "medium" as IconSize,
        url,
        alt,
        paddingIncluded = false
    } = {},
    iconComponent
}: IconProps) => {
    if (url) {
        return (
            <div>
                <Image
                    src={url}
                    alt={alt ?? ""}
                    style={{
                        width: `${iconSizesPaddingIncluded[size]}px`,
                        height: `${iconSizesPaddingIncluded[size]}px`,
                        padding: `${
                            paddingIncluded
                                ? 0
                                : (iconSizesPaddingIncluded[size] -
                                      iconSizesPaddingExcluded[size]) /
                                  2
                        }px`,
                        objectPosition: "center"
                    }}
                />
            </div>
        );
    }
    if (iconComponent) {
        return <div>{iconComponent}</div>;
    }
    return null;
};

const isDarkBackgroundColor = (color?: string): boolean => {
    return color ? isDarkColor(color) : false;
};

export const ContentBlock = ({
    logo,
    eyebrow,
    title,
    text,
    imageProps,
    imageAspectRatio = "16 / 9",
    spotIllustrationUrl,
    icon,
    callToActionProps,
    alignment = "start",
    orientation = "vertical",
    columns = 10,
    backgroundColor,
    hat,
    pants,
    ...props
}: // eslint-disable-next-line complexity
ContentBlockProps) => {
    const isWacRefresh = useWacRefresh();

    // hedge against the value being passed in as a string (the options in uniform can only be string values)
    // which would subsequently fail a string vs number check ie 10 === "10" is false
    const numColumns = Number(columns) as ContentBlockColumns;

    const { titleAs: TitleComponent = "h3", titleAppearsAs } = props;

    const isLargeTextStyling = (TitleComponent as string) === "h2";
    const isSmallDetailStyling = (TitleComponent as string) === "h4";

    const buttonSize: ButtonSize = isSmallDetailStyling ? "small" : "large";

    const uniqueId = useId();
    const titleId =
        (title ? title.replace(/\s+/g, "") : "title") + `-${uniqueId}`;
    const callToActionId =
        (callToActionProps?.link
            ? callToActionProps.link.text.replace(/\s+/g, "")
            : "call-to-action") + `-${uniqueId}`;

    const lightTextStyles = css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            color: `${wacToken("color.neutral.light0")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".body-lg, .body-md, .card-title, .eyebrow-md, .h2, .h3, .h4, .h5": {
            color: `${wacToken("color.neutral.light0")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h2, h3, h4, h5": {
            color: `${wacToken("color.neutral.light0")}`
        }
    });

    const textElement: React.ReactNode =
        typeof text === "string" ? (
            <p
                className={isLargeTextStyling ? "body-lg" : "body-md"}
                css={[
                    zeroMarginCss,
                    isDarkBackgroundColor(backgroundColor) && lightTextStyles
                ]}
            >
                {text}
            </p>
        ) : (
            text
        );

    const bgColorStyle = css({
        "--dxp-block-background-color": backgroundColor
    });

    const backgroundColorMargin = css({
        paddingBlockStart: token("space.400"),
        paddingBlockEnd: token("space.800") // takes into account the existing paddingBlockEnd of space.400
    });

    const textContent = (
        <div
            css={[
                css({
                    display: "flex",
                    flexDirection: "column",
                    gap: token("space.300"),
                    paddingBlockStart:
                        orientation === "horizontal"
                            ? token("space.100")
                            : undefined,
                    justifyContent: "center",
                    [media.above.sm]: {
                        gridColumn: "var(--text-grid-column)"
                    }
                }),
                isDarkBackgroundColor(backgroundColor) && lightTextStyles
            ]}
        >
            <div
                css={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: token("space.100")
                })}
            >
                {logo && (
                    <Image
                        src={ContentBlockLogoLockups[logo].src}
                        alt={ContentBlockLogoLockups[logo].alt}
                        height={ContentBlockLogoLockups[logo].height}
                        style={{
                            objectPosition: "left top",
                            objectFit: "contain"
                        }}
                    />
                )}
                {eyebrow && (
                    <p
                        className={
                            isSmallDetailStyling ? "eyebrow-sm" : "eyebrow-md"
                        }
                        css={zeroMarginCss}
                    >
                        {eyebrow}
                    </p>
                )}
                {title && (
                    <TitleComponent
                        className={titleAppearsAs ? titleAppearsAs : undefined}
                        id={titleId}
                        css={[zeroMarginCss, wordWrapStyles]}
                    >
                        {title}
                    </TitleComponent>
                )}
                {text && textElement}
            </div>
            {callToActionProps &&
                renderCta({
                    ctaProps: callToActionProps,
                    buttonSize,
                    alignment,
                    backgroundColor,
                    titleId,
                    callToActionId
                })}
        </div>
    );

    const assetStyles = css({
        [media.above.sm]: {
            gridColumn: "var(--asset-grid-column)"
        }
    });

    const imageStyle = css({
        // Issue in Picture component applies styles to picture instead of img
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            aspectRatio: imageAspectRatio,
            width: "100%",
            height: "auto",
            objectFit: "contain"
        }
    });

    const roundedCornersStyle = css({
        // Issue in Picture component applies styles to picture instead of img
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            borderRadius: wacToken("corner.radius.card")
        }
    });

    const horizontalSpotIllustrationStyles = css({
        [media.above.sm]: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }
    });

    const assetContent = imageProps ? (
        <div css={[assetStyles]}>
            <Image
                {...imageProps}
                alt={imageProps.alt ?? ""}
                css={[imageStyle, isWacRefresh && roundedCornersStyle]}
            />
        </div>
    ) : spotIllustrationUrl ? (
        <div css={[assetStyles, horizontalSpotIllustrationStyles]}>
            <Image
                src={spotIllustrationUrl}
                alt={""}
                width={236}
                height={236}
            />
        </div>
    ) : icon ? (
        renderIcon(icon)
    ) : null;

    const gapBetweenIconAndText = css({
        gap: token("space.200")
    });

    if (orientation === "vertical") {
        return (
            <div
                css={[
                    bgColorStyle,
                    css({
                        textAlign:
                            typeof alignment === "string"
                                ? alignment
                                : alignment.mobile,
                        [media.above.sm]: {
                            textAlign:
                                typeof alignment === "string"
                                    ? alignment
                                    : alignment.tablet
                        },
                        [media.above.md]: {
                            textAlign:
                                typeof alignment === "string"
                                    ? alignment
                                    : alignment.desktop
                        },
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.300"), // for image or spot illustration
                        background:
                            "var(--dxp-block-background-color, transparent)"
                    }),
                    icon && gapBetweenIconAndText
                ]}
            >
                {assetContent}
                {textContent}
            </div>
        );
    }

    const fullWidthGridStyles = css({
        "marginInline": "auto",
        "maxWidth": "100%",
        "width": "100%",
        "--width-without-gaps": "calc(100% - var(--cl-gap) * 11)", // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
        "paddingInlineStart": `${WCL_GRID_PADDING_SM}px`,
        "paddingInlineEnd": `${WCL_GRID_PADDING_SM}px`,
        [media.only.sm]: {
            paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
            paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
        },
        [media.above.sm]: {
            gridTemplateColumns: "repeat(12, 1fr)"
        },
        [media.above.md]: {
            "--cl-gap": "32px",
            "paddingInlineStart": `${WCL_GRID_PADDING_LG}px`,
            "paddingInlineEnd": `${WCL_GRID_PADDING_LG}px`,
            "maxWidth": `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
        },
        "display": "grid",
        "boxSizing": "border-box",
        "gap": "var(--cl-gap)",
        "--cl-gap": "24px"
    });

    const horizontalContentBlock = (
        <div
            css={[
                fullWidthGridStyles,
                bgColorStyle,
                css({
                    "position": "relative",
                    "zIndex": 1,
                    "paddingBlockEnd": token("space.400"),
                    "--text-grid-column": `${
                        orientation === "horizontal-reverse"
                            ? contentColumnsLayout[numColumns]["contentStart"]
                            : contentColumnsLayout[numColumns]["contentStart"] +
                              contentColumnsLayout[numColumns]["assetSpan"]
                    } / span ${
                        numColumns -
                        contentColumnsLayout[numColumns]["assetSpan"]
                    }`,
                    "--asset-grid-column": `${
                        orientation === "horizontal"
                            ? contentColumnsLayout[numColumns]["contentStart"]
                            : contentColumnsLayout[numColumns]["contentStart"] +
                              numColumns -
                              contentColumnsLayout[numColumns]["assetSpan"]
                    } / span ${contentColumnsLayout[numColumns]["assetSpan"]}`
                }),
                backgroundColor && backgroundColorMargin
            ]}
        >
            {orientation === "horizontal" ? assetContent : null}
            {textContent}
            {orientation === "horizontal-reverse" ? assetContent : null}
        </div>
    );

    switch (numColumns) {
        case 10:
        case 12:
            if (backgroundColor) {
                return (
                    <div
                        css={[
                            bgColorStyle,
                            css({
                                "--hat-height": hat
                                    ? `${SECTION_DIVIDER_HEIGHTS.mobile}px`
                                    : "0px",
                                "--pants-height": pants
                                    ? `${SECTION_DIVIDER_HEIGHTS.mobile}px`
                                    : "0px",
                                [media.above.sm]: {
                                    "--hat-height": hat
                                        ? `${SECTION_DIVIDER_HEIGHTS.tablet}px`
                                        : "0px",
                                    "--pants-height": pants
                                        ? `${SECTION_DIVIDER_HEIGHTS.tablet}px`
                                        : "0px"
                                },
                                [media.above.md]: {
                                    "--hat-height": hat
                                        ? `${SECTION_DIVIDER_HEIGHTS.desktop}px`
                                        : "0px",
                                    "--pants-height": pants
                                        ? `${SECTION_DIVIDER_HEIGHTS.desktop}px`
                                        : "0px"
                                }
                            }),
                            css({
                                width: "100%",
                                position: "relative",
                                marginBlockStart: "var(--hat-height)",
                                marginBlockEnd: "var(--pants-height)"
                            })
                        ]}
                    >
                        <div
                            css={[
                                css({
                                    position: "absolute",
                                    width: "100vw",
                                    [media.above.sm]: {
                                        width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
                                        maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))` // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
                                    },
                                    height: "100%",
                                    insetInlineStart: "50%",
                                    transform: "translateX(-50%)",
                                    background:
                                        "var(--dxp-block-background-color, transparent)"
                                }),
                                hat && hatCommonStyle,
                                hat && hat === "high-right"
                                    ? hatHighRight
                                    : hatHighLeft,
                                pants && pantsCommonStyle,
                                pants && pants === "high-right"
                                    ? pantsHighRight
                                    : pantsHighLeft
                            ]}
                        />
                        {horizontalContentBlock}
                    </div>
                );
            }
            return horizontalContentBlock;
    }
};

const hatCommonStyle = css({
    "::before": {
        content: "",
        position: "relative",
        display: "block",
        height: "var(--hat-height)",
        background: "var(--dxp-block-background-color, transparent)",
        insetBlockStart: "calc(-1 * var(--hat-height))"
    }
});
const hatHighLeft = css({
    "::before": {
        clipPath: "polygon(0 0, 0 100.25%, 100% 100.25%)"
    }
});
const hatHighRight = css({
    "::before": {
        clipPath: "polygon(100% 0, 0 100.25%, 100% 100.25%)"
    }
});

const pantsCommonStyle = css({
    "::after": {
        content: "",
        position: "relative",
        display: "block",
        height: "var(--pants-height)",
        background: "var(--dxp-block-background-color, transparent)",
        insetBlockStart: "calc(100% - var(--hat-height))"
    }
});
const pantsHighLeft = css({
    "::after": {
        clipPath: "polygon(0 100%, 100% -0.25%, 0 -0.25%)"
    }
});
const pantsHighRight = css({
    "::after": {
        clipPath: "polygon(100% 100%, 100% -0.25%, 0 -0.25%)"
    }
});
